import jwt_decode from "jwt-decode";

import { client, persistent } from "../graphql/client";
import {
  ADD_SHOPPING_CART_ITEM_BULK,
  RESET_LOCAL_SHOPPING_CART,
  SET_REFERRAL_DISCOUNT,
  SUBMIT_QUIZ,
  APPLY_DISCOUNT_CODE,
  SET_GUEST_PROMO_CODE_DISCOUNT,
  APPLY_REFERRAL_CODE,
} from "../graphql/mutations";
import {
  GET_LOCAL_SHOPPING_CART,
  GET_MEMBER_INFO_ON_LOGIN,
  GET_MEMBER_CHECKOUT_INFO,
  GET_REFERRAL_DISCOUNT,
  GET_MEMBER_QUIZ_DATA,
  GET_GUEST_PROMO_CODE_DISCOUNT,
} from "../graphql/queries";
import { SET_MEMBER_AUTH } from "../graphql/resolvers/auth";
import URL_PATTERNS from "../urls";
// identifyIterableUser is fired from PageMeta's useEffect once
// memberData?.me populates AND isIterableReady is true — see
// src/components/molecules/PageMeta/index.tsx. Calling it from here
// previously raced the SDK init (the dynamic import in Layout) and
// produced 401s from Iterable's user/update endpoint, since the
// Authorization header is only set after initializeWithConfig has
// registered its JWT callback.
import {
  FETCH_POLICY_CACHE_ONLY,
  HTTP_METHODS,
  ACCESS_TOKEN_EXPIRE_SECONDS,
  STORE_LOCATION_ID_STORAGE_KEY,
} from "./constants";
import { logError } from "./logging";
import { addDate, isDateAfter, parseDate } from "./dates";
import executeRestApi from "./rest";
import {
  formatExtraInfoOnLocalShoppingCartSync,
  setCountryIdOfLoggedInMemberInCache,
  checkIfRequestIsFromStoreLocation,
} from "./tools";
import { sanitiseExtraInfoInput } from "./extraInfo";
import { getCountryIdFromUrl } from "./urls.helper";
import { isCapacitorNative } from "./isCapacitorNative";
import { readAuthToken, writeAuthToken } from "./tokenStorage";
import { calculateLocalCartProductsCost, calculateLocalCartShippingFee } from "./localCart";
import { clearGuestBoxIntent, hasGuestBoxIntent } from "./guestCheckout";
import { clearBuildYourOwnApplied, clearBuildYourOwnIntent, getBuildYourOwnIntent } from "./buildYourOwn";
import { applyBuildYourOwnPromoCode } from "./buildYourOwnApply";
import { showErrorAlert } from "./alerts";
import { clearGuestSubscriptionCheckout, getGuestSubscriptionCheckout } from "./guestSubscriptionCheckout";
import {
  clearGuestSubscriptionTransfer,
  synchroniseGuestSubscriptionCart,
} from "./guestSubscriptionCartSync";

/**
 * Logs user out, removed all data that they have collected.
 *
 * @param {string} [redirectUrl='']
 * @returns {Promise<void>}
 */
const executeLogOut = async (redirectUrl = "") => {
  clearGuestSubscriptionCheckout();
  clearGuestSubscriptionTransfer();
  /* Both build-your-own markers are per-browser, not per-member: left behind,
   * the next member to sign in here would see a set-aside calendar they never
   * chose, or have a refused code applied to their cart at login. */
  clearBuildYourOwnIntent();
  clearBuildYourOwnApplied();
  if (localStorage.getItem(import.meta.env.REACT_APP_STORE_LOCAL_STORAGE)) {
    // Removes all data collected by the user in the localStorage.
    client.resetStore();
    await persistent.purge();

    // Cleans the localStorage for safety, it should be redundant if the commands above worked as expected.
    writeAuthToken(null);
    localStorage.removeItem(import.meta.env.REACT_APP_STORE_LOCAL_STORAGE);
    localStorage.removeItem(STORE_LOCATION_ID_STORAGE_KEY);

    if (redirectUrl) {
      // On native, the WebView is served from https://localhost. Prefixing
      // with REACT_APP_BASE_URL would navigate it to the marketing site,
      // which then bounces to the system browser. Stay in-app with a
      // relative path instead.
      window.location = isCapacitorNative()
        ? redirectUrl
        : `${import.meta.env.REACT_APP_BASE_URL}${redirectUrl}`;
    }
  }
};

/**
 * Forces log out of the current Member. Used when Member is logged out without their intention/action.
 *
 * @returns {Promise<void>}
 */
const forceLogOut = async (redirectUrl) => {
  const target = redirectUrl ?? (isCapacitorNative() ? URL_PATTERNS.WELCOME : URL_PATTERNS.HOME);
  await executeLogOut(target);
};

/**
 * Gets auth information to browser local storage and decodes information.
 * @return {Object}
 * */
const getLocalStorageToken = () => {
  // Creates token object with null properties in case of error or `localStorage` is undefined
  let tokenObject = { accessToken: null, refreshToken: null, email: null };
  const localStorageContent = readAuthToken();

  if (localStorageContent) {
    try {
      tokenObject = JSON.parse(localStorageContent);
    } catch (error) {
      // Token is not a valid JSON string
      try {
        // Check if the token is a legacy signed jwt token instead
        // We can drop support for legacy token and remove the following lines
        // after July 2022 (PLA-463)
        tokenObject = jwt_decode(localStorageContent);
      } catch (decodeError) {
        // Token is not a signed jwt token either
        logError(decodeError);
      }
    }
  }

  return tokenObject;
};

/**
 * Sets encoded auth information to browser local storage.
 * @param {string} accessToken
 * @param {string} refreshToken
 * @param {string} email
 * @param {object} expiresAt
 * */
const setLocalStorageToken = (accessToken, refreshToken, email, expiresAt) => {
  writeAuthToken(
    JSON.stringify({
      accessToken,
      refreshToken,
      email,
      expiresAt,
    })
  );
};

/**
 * Checks if the browser has a store location id store in localStorage.
 *
 * @return {boolean}
 * */
const isPhysicalStoreVisit = () => {
  const storeLocationId = getStoreLocationId();
  return storeLocationId !== null && storeLocationId > 0;
};

const getStoreLocationId = () => {
  let storeLocationId = window.localStorage.getItem(STORE_LOCATION_ID_STORAGE_KEY);
  let storeLocationIdInt = null;
  if (!storeLocationId) {
    storeLocationId = checkIfRequestIsFromStoreLocation();
    // Store the location id here since it is not stored yet.
    if (storeLocationId) {
      storeLocationIdInt = parseInt(storeLocationId, 10);
      window.localStorage.setItem(STORE_LOCATION_ID_STORAGE_KEY, storeLocationIdInt);
    }
  } else {
    storeLocationIdInt = parseInt(storeLocationId, 10);
  }

  return storeLocationIdInt;
};

/**
 * Checks if the browser has an access token store in localStorage.
 * If checkExpiration is set to true then expiration of the token is checked as well.
 *
 * @param {boolean} checkExpiration
 * @return {boolean}
 * */
const isLoggedIn = (checkExpiration = false) => {
  const localStorageTokenObject = getLocalStorageToken();
  const localStorageContent = readAuthToken();

  const doTokensExist = Boolean(localStorageContent && localStorageTokenObject);

  // Used for components (eg. Header) to avoid presenting that Member is logged out when token is being
  // refreshed. The same applies for components that redirect Member in case they're not logged in.
  // If refreshing a token failed Member will be logged out anyway by handleError in refreshTokenLink.
  if (!checkExpiration) {
    return doTokensExist;
  }

  return Boolean(doTokensExist && isDateAfter(parseDate(localStorageTokenObject.expiresAt), parseDate()));
};

/**
 * @typedef {Object} ShoppingCartSyncResult
 * @property {"empty"|"success"|"partial"|"error"} status
 * @property {Array<Object>} failedItems
 * @property {Array<Object|Error|null>} errors
 * @property {Object|null|undefined} [shoppingCart]
 */

const shoppingCartSyncPromises = new Map();

/**
 * Synchronises Products from the local state (added when user was not authenticated) with the database.
 *
 * @param {number} memberId
 * @param {{minBottlesForFreeShipping?: number|null,
 *   standardShippingFee?: number|null}} cartPricing
 * @returns {Promise<ShoppingCartSyncResult>} transfer outcome; authentication itself is never
 *   rolled back by cart errors
 */
const transferLocalShoppingCart = async (memberId, cartPricing = {}) => {
  if (getGuestSubscriptionCheckout()) {
    return synchroniseGuestSubscriptionCart(client, memberId, cartPricing);
  }
  const localShoppingCartQuery = await client.query({
    query: GET_LOCAL_SHOPPING_CART,
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });

  const shoppingCart = localShoppingCartQuery.data && localShoppingCartQuery.data.localShoppingCart;

  if (!shoppingCart?.items?.length) {
    try {
      await client.query({ query: GET_MEMBER_CHECKOUT_INFO, fetchPolicy: "network-only" });
      return { status: "empty", failedItems: [], errors: [] };
    } catch (error) {
      logError(error, { location: "synchroniseShoppingCart", action: "refetchEmptyCart" });
      return { status: "error", failedItems: [], errors: [error] };
    }
  }

  const snapshot = shoppingCart.items.slice();
  const items = snapshot.map((item) => {
    // Drop Apollo metadata and server-only keys the backend input object does not accept.
    const formattedExtraInfo = sanitiseExtraInfoInput(
      formatExtraInfoOnLocalShoppingCartSync(item.extraInfo || {})
    );
    return {
      productId: item.product.id,
      quantity: item.quantity,
      extraInfo: formattedExtraInfo || null,
      redeemWithPoints: false,
    };
  });

  // A guest who chose the subscription-box path gets their wines hydrated into a
  // first box instead of the one-off cart (ignored server-side for anyone who has
  // been subscribed before). A build-your-own advent calendar overrides that: its
  // promo code only counts wines in the one-off cart and is refused on subscriptions.
  const subscriptionIntent = hasGuestBoxIntent() && !getBuildYourOwnIntent();

  let response;
  try {
    response = await client.mutate({
      mutation: ADD_SHOPPING_CART_ITEM_BULK,
      variables: { input: { memberId, items, subscriptionIntent } },
    });
  } catch (error) {
    logError(error, { location: "synchroniseShoppingCart", action: "bulkTransfer" });
    return { status: "error", failedItems: snapshot, errors: [error] };
  }

  const payload = response?.data?.bulkAddShoppingCartItems;
  const errors = payload?.errors || [];
  const failedItemsCount = payload?.failedItemsCount || 0;
  const isCompleteTransfer =
    failedItemsCount === 0 &&
    payload?.successfulItemsCount === snapshot.length &&
    payload?.totalItemsCount === snapshot.length;

  let status = "success";
  let failedItems = [];
  if (isCompleteTransfer) {
    // The mutation response confirms every snapshotted item reached the member cart. Clearing
    // before the refetch prevents a later refetch retry from adding those quantities twice.
    await client.mutate({ mutation: RESET_LOCAL_SHOPPING_CART });
    clearGuestBoxIntent();
  } else {
    const failedIndexes = new Set(
      errors
        .map((error) => /^item_(\d+)_/.exec(error?.field || ""))
        .filter(Boolean)
        .map((match) => Number(match[1]))
    );
    failedItems = failedIndexes.size ? snapshot.filter((item, index) => failedIndexes.has(index)) : snapshot;
    status = "partial";

    const hasPricing =
      Number.isFinite(cartPricing.minBottlesForFreeShipping) &&
      Number.isFinite(cartPricing.standardShippingFee);
    client.writeQuery({
      query: GET_LOCAL_SHOPPING_CART,
      data: {
        localShoppingCart: {
          ...shoppingCart,
          visible: false,
          items: failedItems,
          totalProductsCost: calculateLocalCartProductsCost(failedItems),
          totalShippingFee: hasPricing
            ? calculateLocalCartShippingFee(
                failedItems,
                cartPricing.minBottlesForFreeShipping,
                cartPricing.standardShippingFee
              )
            : shoppingCart.totalShippingFee,
        },
      },
    });
  }

  try {
    await client.query({ query: GET_MEMBER_CHECKOUT_INFO, fetchPolicy: "network-only" });
  } catch (error) {
    logError(error, { location: "synchroniseShoppingCart", action: "refetchCheckout" });
    return {
      status: "error",
      failedItems,
      errors: [...errors, error],
      shoppingCart: payload?.shoppingCart,
    };
  }

  return { status, failedItems, errors, shoppingCart: payload?.shoppingCart };
};

/**
 * Applies the build-your-own advent code a guest chose on the advent page, once their
 * wines are in the member cart. The choice is cleared only when the code is on the
 * cart; a refusal shows the backend's reason and keeps the choice, so the next cart
 * sync (or choosing again on the advent page) can retry once the cause is fixed. A
 * member cart already holding things a wines-only order cannot keep is the same: it
 * is nobody's to empty here, so the alert names them and the choice stays.
 * Dropped inside a guest subscription checkout — those wines went into a first box,
 * where the code cannot apply.
 *
 * @param {number} memberId
 * @returns {Promise<void>}
 */
const applyPendingBuildYourOwn = async (memberId) => {
  const intent = getBuildYourOwnIntent();
  if (!intent) return;
  if (getGuestSubscriptionCheckout()) {
    clearBuildYourOwnIntent();
    return;
  }

  const outcome = await applyBuildYourOwnPromoCode(client, memberId, intent.promoCode, {
    perNumberOfWines: intent.perNumberOfWines,
  });
  if (outcome.ok) {
    clearBuildYourOwnIntent();
    return;
  }
  const reason = outcome.blockedBy
    ? `your cart already holds ${outcome.blockedBy.join(", ")}, and a build-your-own calendar is a ` +
      "wines-only order. Remove them and choose Build your own again."
    : outcome.message;
  showErrorAlert(`We couldn't start your build-your-own advent calendar: ${reason}`, { autoClose: 10000 });
};

const performShoppingCartSync = async (memberId, cartPricing = {}) => {
  const result = await transferLocalShoppingCart(memberId, cartPricing);
  if (result.status !== "error") await applyPendingBuildYourOwn(memberId);
  return result;
};

/**
 * Coalesces concurrent post-login calls so the same local snapshot cannot be added twice.
 * A completed partial transfer may still be retried later with only its retained failed items.
 *
 * @param {number} memberId
 * @param {{minBottlesForFreeShipping?: number|null, standardShippingFee?: number|null}} cartPricing
 * @returns {Promise<ShoppingCartSyncResult>}
 */
const synchroniseShoppingCart = (memberId, cartPricing = {}) => {
  if (shoppingCartSyncPromises.has(memberId)) return shoppingCartSyncPromises.get(memberId);

  const syncPromise = performShoppingCartSync(memberId, cartPricing).finally(() => {
    shoppingCartSyncPromises.delete(memberId);
  });
  shoppingCartSyncPromises.set(memberId, syncPromise);
  return syncPromise;
};

/**
 * Synchronises Promo Code from the local state (added before user signed up/ logged in) with the database.
 *
 * @param {number} memberId
 * @returns {Promise<void>}
 */
const synchronisePromoCodeDiscount = async (memberId) =>
  client
    .query({
      query: GET_GUEST_PROMO_CODE_DISCOUNT,
      options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
    })
    .then((promoCodeQuery) => {
      const { data } = promoCodeQuery;

      if (data === null) {
        // Sometimes 'data' is null, it shouldn't be the case as defaults are set for
        // this query (possible bug)
        logError(["Promo Code query empty.", promoCodeQuery]);
        return Promise.resolve();
      }

      const { promoCode, discountValue } = data.guestPromoCodeDiscount;

      if (discountValue) {
        return client
          .mutate({
            mutation: APPLY_DISCOUNT_CODE,
            variables: {
              input: {
                memberId,
                name: promoCode,
              },
            },
          })
          .then(() =>
            // Resets Promo Code discount once it's applied in the database
            client.mutate({
              mutation: SET_GUEST_PROMO_CODE_DISCOUNT,
              variables: {
                promoCode: null,
                discountValue: 0,
              },
            })
          );
      }
      return Promise.resolve();
    });

/**
 * Synchronises Referral Code from the local state (added before user signed up/ logged in) with the database.
 *
 * @param memberId
 * @returns {Promise<void>}
 */
const synchroniseReferralDiscount = async (memberId) =>
  client
    .query({
      query: GET_REFERRAL_DISCOUNT,
      options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
    })
    .then((referralDiscountQuery) => {
      const { data } = referralDiscountQuery;

      const { referralCode } = data.referralDiscount;

      if (referralCode) {
        return client
          .mutate({
            mutation: APPLY_REFERRAL_CODE,
            variables: {
              input: {
                memberId,
                referralCode,
              },
            },
          })
          .then(() =>
            // Resets Referral Code once it's applied in the database
            client.mutate({
              mutation: SET_REFERRAL_DISCOUNT,
              variables: {
                referralCode: null,
                giveawayCode: null,
              },
            })
          );
      }
      return Promise.resolve();
    });

/**
 * Refreshes auth local cache used by apollo client.
 *
 * * @param memberId
 * */
const refreshAuthTokensInApolloCache = async (memberId) => {
  await client.mutate({
    mutation: SET_MEMBER_AUTH,
    variables: {
      memberId,
      token: readAuthToken(),
    },
  });
};

/**
 * Saves Member ID to the local state and synchronises the local shopping cart (of unauthenticated user)
 * with the database.
 *
 * @param {number} memberId - id from member
 * @param {Array} shoppingCartItemSet - shopping cart items
 * */
const handleUpdatesOnLogin = async (memberId, cartPricing = {}) => {
  // WARNING: client.mutate and client.query seem to not return a Promise, thus 'await' seems to take
  // no effect. Even if they were a Promise there would be a risk the don't handle refetchQueries in scope
  // of the same Promise.

  // Sets values related to member's Auth
  await refreshAuthTokensInApolloCache(memberId);

  const guestStateResults = await Promise.allSettled([
    synchronisePromoCodeDiscount(memberId),
    synchroniseReferralDiscount(memberId),
  ]);
  guestStateResults.forEach((result) => {
    if (result.status === "rejected") {
      logError(result.reason, { location: "handleUpdatesOnLogin", action: "syncGuestDiscount" });
    }
  });
  return synchroniseShoppingCart(memberId, cartPricing);
};

const executeRefreshTokenRequest = async () => {
  // Creates arguments to pass into Login API request
  const urlPath = `${import.meta.env.REACT_APP_REST_AUTH_PATH}`;
  const localStorageTokenObject = getLocalStorageToken();
  const data = {
    grant_type: "refresh_token",
    refresh_token: localStorageTokenObject.refreshToken,
    client_id: `${import.meta.env.REACT_APP_CLIENT_ID}`,
  };

  // gets a new access token using refresh token
  const refreshTokenResponse = await executeRestApi(HTTP_METHODS.POST, urlPath, { data });

  await setLocalStorageToken(
    refreshTokenResponse.access_token,
    refreshTokenResponse.refresh_token,
    localStorageTokenObject.email,
    addDate(parseDate(), refreshTokenResponse.expires_in, "second")
  );

  // Needed for compatibility with TokenRefetchLink
  refreshTokenResponse.text = () => new Promise((resolve) => resolve(JSON.stringify(refreshTokenResponse)));
  return refreshTokenResponse;
};

/**
 * This function is called when user logs in,
 * and deletes all locally saved quiz data.
 * */
const removeSavedQuizDataFromLocalStorage = () => {
  window.localStorage.removeItem("nonMemberQuizAnswersSelected");
  window.localStorage.removeItem("nonMemberQuizVersion");
  window.localStorage.removeItem("nonMemberQuizQuestion");
  window.localStorage.removeItem("nonMemberSavedName");
  window.localStorage.removeItem("nonMemberQuizWelcomePackId");
  window.localStorage.removeItem("nonMemberQuizFreeWineColorId");
  window.localStorage.removeItem("redBottles");
  window.localStorage.removeItem("whiteBottles");
  window.localStorage.removeItem("roseBottles");
  window.localStorage.removeItem("sparklingBottles");
};

/**
 * compares the users country id with the URLs country id.
 * if they're the same, store the id in local store
 * else, force log out the user.
 *
 * @param {object} userCountry
 * */
const validateUserCountryIdAndLogoutIfFails = (userCountry) => {
  const { id } = userCountry || { id: 0 };
  const urlCountryId = getCountryIdFromUrl();

  // If there is no country in the URL, don't set country ID in cache and don't force logout user.
  if (!urlCountryId) return;
  if (id === urlCountryId) {
    setCountryIdOfLoggedInMemberInCache(id);
  } else {
    forceLogOut();
  }
};

/**
 * Sets up local cache right after logging in such as auth cache, synchronising local cart cache with
 * remote cart data fetched from backend, setups user profile.
 *
 * @param {string} email
 * @param {object} tokenObj
 * @param {object} memberData
 * @param {{onShoppingCartSync?: function, cartPricing?: object}} options
 * @returns {int|null}
 * */
const setupLocalAccountAndCleanUpPostLogin = async (email, tokenObj, memberData, options = {}) => {
  if (!tokenObj) return null;

  // eslint-disable-next-line camelcase
  const { access_token, refresh_token, expires_in } = tokenObj;
  // eslint-disable-next-line camelcase
  const expiresAt = addDate(parseDate(), expires_in || ACCESS_TOKEN_EXPIRE_SECONDS, "second");

  // Encodes and Stores tokens in localStorage --> https://www.npmjs.com/package/jsonwebtoken
  await setLocalStorageToken(access_token, refresh_token, email, expiresAt);

  // Member details that need to be saved to the local state
  const { id } = memberData || {};
  if (memberData && memberData.externalUniqueId) {
    // Save urlCountryId as part of the sign up process.
    // This will be used later to force log out the user if he tries to switch country.
    const urlCountryId = getCountryIdFromUrl();
    setCountryIdOfLoggedInMemberInCache(urlCountryId);
  }

  // If Member data is not specified gets basic info from the API
  let memberId = id;
  if (!memberId) {
    const memberInfoQuery = await client.query({
      query: GET_MEMBER_INFO_ON_LOGIN,
      options: { partialRefetch: true },
    });
    if (memberInfoQuery.data) {
      const { me } = memberInfoQuery.data;
      memberId = me && me.id;
      const { country } = me || {};
      validateUserCountryIdAndLogoutIfFails(country);
    }
  }

  // Setups local cache and syncs cart data between backend and local cache
  const shoppingCartSyncResult = await handleUpdatesOnLogin(memberId, options.cartPricing);
  options.onShoppingCartSync && options.onShoppingCartSync(shoppingCartSyncResult);

  removeSavedQuizDataFromLocalStorage();

  return memberId;
};

/**
 * Executes Login api request and save token details.
 * @param {string} email
 * @param {string} password
 * @param {object} memberData
 * @return {object}
 * */
const executeLogInRequest = async (email, password, memberData = null) => {
  // Creates arguments to pass into Login API request
  const urlPath = `${import.meta.env.REACT_APP_REST_AUTH_PATH}`;
  const data = {
    password,
    username: email,
    grant_type: "password",
    client_id: `${import.meta.env.REACT_APP_CLIENT_ID}`,
  };

  // Logs user in, if sign up is successful
  const loginResponse = await executeRestApi(HTTP_METHODS.POST, urlPath, {
    data,
  });
  await setupLocalAccountAndCleanUpPostLogin(email, loginResponse, memberData);

  return loginResponse;
};

/**
 * Sends a stand-alone login mutation to fetch new tokens and refresh local auth cache in cases such as
 * after password is updated which renders existing tokens void immediately.
 *
 * @param {string} email
 * @param {string} password
 * @param {string} memberId
 * @return {object}
 * */
const refreshTokensAfterPasswordUpdate = async (email, password, memberId) => {
  // Creates arguments to pass into Login API request
  const urlPath = `${import.meta.env.REACT_APP_REST_AUTH_PATH}`;
  const data = {
    password,
    username: email,
    grant_type: "password",
    client_id: `${import.meta.env.REACT_APP_CLIENT_ID}`,
  };

  // Logs user in, if sign up is successful
  const loginResponse = await executeRestApi(HTTP_METHODS.POST, urlPath, {
    data,
  });
  if (loginResponse) {
    // eslint-disable-next-line camelcase
    const { access_token, refresh_token, expires_in } = loginResponse;
    const expiresIn = addDate(parseDate(), expires_in, "second");
    await setLocalStorageToken(access_token, refresh_token, email, expiresIn);

    await refreshAuthTokensInApolloCache(memberId);
  }

  return loginResponse;
};

/**
 * Sends sign up request to GraphQL mutation and log results in the browser
 * On successful sign up it saves local shopping cart item to database
 * @param {Object} form
 * @param {Function} signUp
 * @param {Function} addShoppingCart
 * @param {Function} setMemberAuth
 * @param {Function} resetLocalShoppingCart
 * @param {Function} localShoppingCart
 * @return {Promise<void>} response object built with responses from all promises.
 * */
const executeSignUpRequest = async (
  form,
  signUp,
  addShoppingCart,
  setMemberAuth,
  resetLocalShoppingCart,
  localShoppingCart
) => {
  const response = {};
  const shoppingCart = localShoppingCart;

  // Creates an array (signUpInput) removing unnecessary info
  const { __typename, confirmPassword, ...signUpInput } = form;

  // Builds input depending on which page the user is accessing
  const input = { ...signUpInput, hasUpdatedPassword: true };

  if (confirmPassword === signUpInput.password) {
    // Saves new member (signUp)
    await signUp({ variables: { input } }).then((member) => {
      response.member = member;
    });

    // Only executes login in case SignUp mutation returns no error
    if (!response.member.data.signUp.errors) {
      // Saves Member ID to the state
      await setMemberAuth({
        variables: {
          memberId: response.member.data.signUp.id,
          token: null, // token not available yet
        },
      });

      // Executes Login and redirects user to MyAccount page
      try {
        await executeLogInRequest(form.email, form.password);
      } catch (errorData) {
        const { error_description: errorMessage } = errorData || {};

        // Catches error from server (if login unsuccessful) and show message in the form
        // Stores error from server's response in state variables
        response.form = {
          ...form,
          error: [errorMessage || "Sorry, something went wrong!"],
        };
      }
      if (shoppingCart && shoppingCart.items.length > 0) {
        // Creates shopping cart in database with local storage item
        await shoppingCart.items.forEach((item) =>
          addShoppingCart({
            variables: {
              input: {
                memberId: response.member.data.signUp.id,
                productId: item.product.id,
                quantity: item.quantity,
              },
            },
          })
        );
        resetLocalShoppingCart();
      }
    }
  } else {
    response.errors = ["Sorry, your passwords do not match."];
  }
  return response;
};

const runSubmitQuizMutation = async ({
  answersIds,
  firstName,
  email,
  redBottles,
  whiteBottles,
  roseBottles,
  sparklingBottles,
  shouldWinesBeAddedToSubscription,
  welcomePackId,
  preferredWineClassId,
  locationId,
}) => {
  // Auth data required for user authentication
  const authData = {
    clientId: `${import.meta.env.REACT_APP_CLIENT_ID}`,
  };

  let mutationResponse = null;

  await client
    .mutate({
      mutation: SUBMIT_QUIZ,
      variables: {
        input: {
          answersIds,
          firstName,
          email,
          redBottles,
          whiteBottles,
          roseBottles,
          sparklingBottles,
          shouldWinesBeAddedToSubscription,
          welcomePackId,
          preferredWineClassId,
          locationId,
          ...authData,
        },
      },
      refetchQueries: isLoggedIn() ? () => [{ query: GET_MEMBER_QUIZ_DATA }] : [],
    })
    .then((response) => {
      mutationResponse = response;
    })
    .catch((error) => {
      logError(error);
    });

  return mutationResponse;
};

export {
  executeRefreshTokenRequest,
  executeLogInRequest,
  refreshTokensAfterPasswordUpdate,
  executeSignUpRequest,
  getLocalStorageToken,
  isLoggedIn,
  forceLogOut,
  executeLogOut,
  runSubmitQuizMutation,
  setupLocalAccountAndCleanUpPostLogin,
  synchroniseShoppingCart,
  isPhysicalStoreVisit,
  getStoreLocationId,
};
